import { useCallback, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import { CheckboxRenderer } from "@/controls/ag-grid/components/CheckboxRenderer";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useProductsWithNoRecipe } from "../hooks/useProductsWithNoRecipe";
import type { ProductWithNoRecipeRow } from "../models/ProductsWithNoRecipe";

const COLUMN_DEFS: ColDef<ProductWithNoRecipeRow>[] = [
	{
		field: "ProductSizeName",
		headerName: "Product Size",
		flex: 1,
		minWidth: 160,
	},
	{
		field: "ProductTypeName",
		headerName: "Product Type",
		flex: 1,
		minWidth: 160,
	},
	{ field: "SectionCode", headerName: "Section Code", flex: 1, minWidth: 140 },
];

// The procedure returns an Active flag, so the column and the "Show inactive"
// toggle are part of the screen. The legacy procedure had no such column, which
// is why they used to be hidden.
const ACTIVE_COLUMN: ColDef<ProductWithNoRecipeRow> = {
	field: "Active",
	headerName: "Active",
	width: 90,
	cellStyle: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	cellRenderer: CheckboxRenderer,
};

const sortDistinct = (values: (string | null)[]) =>
	Array.from(new Set(values.filter((v): v is string => !!v))).sort((a, b) =>
		a.localeCompare(b),
	);

// Filters (Product Type must be picked first; Product Size cascades from it).
// Filtering is client-side over the fetched rows, so it works the same in
// legacy and new (revamped) modes — no SP/proxy/flag changes needed. The values are
// names, not ids: the page has no catalog behind it.
const URL_SCHEMA = {
	productType: urlParam.string(""),
	productSize: urlParam.string(""),
	sectionCode: urlParam.string(""),
	// Off by default: only active products are shown (the actionable ones).
	showInactive: urlParam.bool(false),
};

export const ProductsWithNoRecipeReport = () => {
	const [errorMsg, setErrorMsg] = useState("");
	const [{ productType, productSize, sectionCode, showInactive }, setFilters] =
		useUrlState(URL_SCHEMA);
	const [sectionCodeText, setSectionCodeText] = useUrlText(
		sectionCode,
		useCallback((value) => setFilters({ sectionCode: value }), [setFilters]),
	);
	const gridApiRef = useRef<GridApi<ProductWithNoRecipeRow> | null>(null);

	const dataQuery = useProductsWithNoRecipe();
	const rows = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);

	const columnDefs = useMemo(() => [...COLUMN_DEFS, ACTIVE_COLUMN], []);

	// Dropdown options derived from the data (only types/sizes that actually
	// have products without a recipe). Product Size cascades from Product Type.
	const productTypeOptions = useMemo(
		() => sortDistinct(rows.map((r) => r.ProductTypeName)),
		[rows],
	);
	const productSizeOptions = useMemo(
		() =>
			sortDistinct(
				rows
					.filter((r) => !productType || r.ProductTypeName === productType)
					.map((r) => r.ProductSizeName),
			),
		[rows, productType],
	);

	const filteredRows = useMemo(() => {
		const section = sectionCode.trim().toLowerCase();
		return rows.filter(
			(r) =>
				(showInactive || r.Active !== false) &&
				(!productType || r.ProductTypeName === productType) &&
				(!productSize || r.ProductSizeName === productSize) &&
				(!section || (r.SectionCode ?? "").toLowerCase().includes(section)),
		);
	}, [rows, productType, productSize, sectionCode, showInactive]);

	const handleGridReady = useCallback(
		(e: GridReadyEvent<ProductWithNoRecipeRow>) => {
			gridApiRef.current = e.api;
		},
		[],
	);

	const handleExportExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "Products_Without_Recipe.xlsx",
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Products with no Recipe
			</Typography>

			<Paper
				elevation={3}
				sx={{
					p: 1.5,
					display: "flex",
					gap: 1.5,
					alignItems: "center",
					flexWrap: "wrap",
				}}
			>
				<Box sx={{ width: 180 }}>
					<TextField
						select
						size="small"
						label="Product Type"
						fullWidth
						value={productType}
						onChange={(e) => {
							const v = e.target.value;
							// Clear the size if it no longer belongs to the chosen type.
							const keepSize =
								!productSize ||
								rows.some(
									(r) =>
										r.ProductSizeName === productSize &&
										(v === "" || r.ProductTypeName === v),
								);
							setFilters({
								productType: v,
								productSize: keepSize ? productSize : "",
							});
						}}
					>
						<MenuItem value="">All</MenuItem>
						{productTypeOptions.map((name) => (
							<MenuItem key={name} value={name}>
								{name}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box sx={{ width: 220 }}>
					<TextField
						select
						size="small"
						label="Product Size"
						fullWidth
						value={productSize}
						onChange={(e) => setFilters({ productSize: e.target.value })}
					>
						<MenuItem value="">All</MenuItem>
						{productSizeOptions.map((name) => (
							<MenuItem key={name} value={name}>
								{name}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box sx={{ width: 180 }}>
					<TextField
						size="small"
						label="Section Code"
						fullWidth
						value={sectionCodeText}
						onChange={(e) => setSectionCodeText(e.target.value)}
					/>
				</Box>

				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={showInactive}
							onChange={(e) => setFilters({ showInactive: e.target.checked })}
						/>
					}
					label="Show inactive"
					slotProps={{ typography: { variant: "body2" } }}
				/>

				<Typography variant="body2" color="text.secondary">
					{filteredRows.length} product(s)
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Button
					size="small"
					variant="outlined"
					startIcon={<RefreshIcon />}
					onClick={() => dataQuery.refetch()}
					disabled={dataQuery.isFetching}
				>
					Refresh
				</Button>
				<Button
					size="small"
					variant="outlined"
					startIcon={<DownloadIcon />}
					onClick={handleExportExcel}
				>
					Export
				</Button>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<ProductWithNoRecipeRow>
						rowData={filteredRows}
						columnDefs={columnDefs}
						defaultColDef={{ resizable: true, sortable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
